import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { pdf } from '@react-pdf/renderer';
import { CheckCircle2, Download, FileText } from 'lucide-react';
import { db, storage, ensureAuth } from '../utils/firebase';
import { getPublicSettings } from '../utils/appSettings';
import type { ChangeOrder, ChangeOrderAuditEntry, ProjectPLBucket } from '../types';
import { loadChangeOrderLedgerContext, priorContractValueForChangeOrder } from '../utils/contractValue';
import { changeOrderBillsImmediately } from '../utils/paymentSchedule';
import { syncApprovedChangeOrder } from '../utils/changeOrderSync';
import { getChangeOrderReference, getChangeOrderBySlug } from '../utils/changeOrders';
import { calculateTotals } from '../utils/proposalCalculations';
import { formatBondingPercent } from '../utils/proposalBonding';
import { logPortalActivity } from '../utils/portalActivityLogger';
import SignaturePad from '../components/engagement/SignaturePad';
import ChangeOrderPdfDocument from '../components/proposals/ChangeOrderPdfDocument';
import ChangeOrderInternalBadge, { isInternallyApprovedChangeOrder } from '../components/proposals/ChangeOrderInternalBadge';
import '../components/proposals/ChangeOrder.css';
import './PublicProposalPage.css';

const BUCKET_LABELS: Record<ProjectPLBucket, string> = {
  design: 'Design',
  engineering: 'Engineering',
  timber: 'Timber / Materials',
  production: 'Production',
  freight: 'Freight',
  install: 'Install',
  travel: 'Travel',
  overhead: 'Overhead',
  unassigned: 'Other',
};

const fmt = (n: number) => `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;

async function fetchLogoBase64(): Promise<string | undefined> {
  try {
    const settings = await getPublicSettings();
    if (settings.engagementLogo?.url) {
      const url = settings.engagementLogo.url.includes('?')
        ? `${settings.engagementLogo.url}&t=${Date.now()}`
        : `${settings.engagementLogo.url}?t=${Date.now()}`;
      const response = await fetch(url, { mode: 'cors', cache: 'no-store', credentials: 'omit' });
      if (response.ok) {
        const blob = await response.blob();
        return await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('FileReader failed'));
          reader.readAsDataURL(blob);
        });
      }
    }
  } catch {
    /* fall through to fallback */
  }
  try {
    const fallback = await fetch('/assets/logo/LC_pdf_logo.png');
    if (fallback.ok) {
      const blob = await fallback.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('FileReader failed'));
        reader.readAsDataURL(blob);
      });
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

export default function PublicChangeOrderPage() {
  const { slug } = useParams<{ slug: string }>();
  // Staff preview (review modal iframe): show drafts read-only, hide the signature pad.
  const previewMode = useMemo(
    () => new URLSearchParams(window.location.search).get('preview') === '1',
    [],
  );
  const [co, setCo] = useState<ChangeOrder | null>(null);
  const [coId, setCoId] = useState<string | null>(null);
  const [priorContractValue, setPriorContractValue] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [companyBackground, setCompanyBackground] = useState<{ url: string; name: string } | null>(null);

  // Portal/engagement background image (matches the proposal + portal experience).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensureAuth();
        const settings = await getPublicSettings();
        if (!cancelled && settings?.engagementBackground) {
          setCompanyBackground(settings.engagementBackground);
        }
      } catch {
        /* background is decorative — ignore failures */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Initial load by slug.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        await ensureAuth();
        const found = await getChangeOrderBySlug(slug);
        if (cancelled) return;
        if (!found || !found.id) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setCo(found);
        setCoId(found.id);

        // Prior contract value: the approval snapshot when stamped, otherwise
        // base + COs approved BEFORE this one (never later ones). Drafts see
        // the live contract total.
        try {
          const ctx = await loadChangeOrderLedgerContext(found.dealId);
          if (!cancelled) setPriorContractValue(priorContractValueForChangeOrder(found, ctx.base, ctx.changeOrders));
        } catch {
          /* ignore */
        }
      } catch (err) {
        console.error('[PublicChangeOrder] load failed', err);
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Live updates.
  useEffect(() => {
    if (!coId) return;
    const unsub = onSnapshot(doc(db, 'changeOrders', coId), snap => {
      if (snap.exists()) setCo({ id: snap.id, ...(snap.data() as ChangeOrder) });
    });
    return unsub;
  }, [coId]);

  // Once the approval snapshot lands (client stamp or server safety net), it
  // is the source of truth for what this CO showed when it was signed.
  useEffect(() => {
    if (typeof co?.contractValueBefore === 'number' && Number.isFinite(co.contractValueBefore)) {
      setPriorContractValue(co.contractValueBefore);
    }
  }, [co?.contractValueBefore]);

  const rows = useMemo(() => {
    if (!co) return [] as { desc: string; cat: string; amount: number }[];
    if (co.pricingMode === 'line_item' && co.lineItems) {
      return co.lineItems.map(li => ({
        desc: li.description || 'Item',
        cat: BUCKET_LABELS[li.bucket] || 'Other',
        amount: li.amount,
      }));
    }
    if (co.pricingMode === 'builder' && co.builderNodeSets) {
      const margin = co.builderProfitMargin ?? 0;
      return co.builderNodeSets
        .filter(ns => ns.enabled !== false)
        .map(ns => ({
          desc: ns.name || 'Structure',
          cat: 'Structure',
          amount: calculateTotals([ns], margin).billableTotal,
        }));
    }
    return [{ desc: co.title || 'Change order', cat: 'Lump sum', amount: co.deltaAmount - (co.bondingAdjustment || 0) }];
  }, [co]);

  // Bonding pass-through that rides on this change (0 on COs priced without bonding).
  const bondingAdjustment = Number(co?.bondingAdjustment) || 0;
  const scopeDelta = (co?.deltaAmount ?? 0) - bondingAdjustment;

  const newContractValue = priorContractValue + (co?.deltaAmount ?? 0);
  const isSigned = co?.status === 'approved' || !!co?.signature;

  const handleSign = useCallback(
    async (signatureData: { dataUrl: string; signedBy: string }) => {
      if (!co || !coId) return;
      setSigning(true);
      setSignError(null);
      try {
        await ensureAuth();
        const signedAt = Timestamp.now();
        const audit: ChangeOrderAuditEntry = {
          at: new Date(),
          byUid: 'client',
          byName: signatureData.signedBy,
          action: 'signed',
          fromStatus: co.status,
          toStatus: 'approved',
          amount: co.deltaAmount,
        };
        await updateDoc(doc(db, 'changeOrders', coId), {
          signature: { dataUrl: signatureData.dataUrl, signedBy: signatureData.signedBy, signedAt },
          status: 'approved',
          approvedAt: signedAt,
          auditLog: [...(co.auditLog || []), audit],
          updatedAt: signedAt,
        });

        // Generate + upload signed PDF.
        try {
          const logo = await fetchLogoBase64();
          const signedCo: ChangeOrder = {
            ...co,
            signature: { dataUrl: signatureData.dataUrl, signedBy: signatureData.signedBy, signedAt: new Date() },
            status: 'approved',
          };
          const blob = await pdf(
            <ChangeOrderPdfDocument changeOrder={signedCo} companyLogo={logo} priorContractValue={priorContractValue} />,
          ).toBlob();
          const path = `signed-change-orders/${co.dealId}/${coId}-signed.pdf`;
          const storageRef = ref(storage, path);
          await uploadBytes(storageRef, blob);
          const signedPdfUrl = await getDownloadURL(storageRef);
          await updateDoc(doc(db, 'changeOrders', coId), { signedPdfUrl });
        } catch (pdfErr) {
          console.warn('[PublicChangeOrder] signed PDF generation failed', pdfErr);
        }

        // Apply side effects (contract value, sales order, activity).
        try {
          await syncApprovedChangeOrder(coId);
        } catch (syncErr) {
          console.warn('[PublicChangeOrder] approval sync failed', syncErr);
        }

        // Best-effort portal activity log.
        if (co.projectId) {
          try {
            await logPortalActivity(
              co.dealId,
              co.projectId,
              'action',
              'signed_change_order',
              { signedBy: signatureData.signedBy, deltaAmount: co.deltaAmount, changeOrderNumber: getChangeOrderReference(co) },
              'client',
            );
          } catch {
            /* ignore */
          }
        }
      } catch (err) {
        console.error('[PublicChangeOrder] sign failed', err);
        setSignError('Something went wrong while saving your signature. Please try again.');
      } finally {
        setSigning(false);
      }
    },
    [co, coId, priorContractValue],
  );

  const handleDownload = useCallback(async () => {
    if (!co) return;
    if (co.signedPdfUrl) {
      window.open(co.signedPdfUrl, '_blank');
      return;
    }
    try {
      const logo = await fetchLogoBase64();
      const blob = await pdf(
        <ChangeOrderPdfDocument changeOrder={co} companyLogo={logo} priorContractValue={priorContractValue} />,
      ).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error('[PublicChangeOrder] download failed', err);
    }
  }, [co, priorContractValue]);

  const background = (
    <>
      {companyBackground && (
        <div className="proposal-background" style={{ backgroundImage: `url(${companyBackground.url})` }} />
      )}
      <div className="proposal-background-overlay" />
    </>
  );

  if (loading) {
    return (
      <div className="proposal-public-page">
        {background}
        <div className="proposal-container" style={{ textAlign: 'center', paddingTop: 80 }}>
          <p className="co-public-muted">Loading change order…</p>
        </div>
      </div>
    );
  }

  if (notFound || !co) {
    return (
      <div className="proposal-public-page">
        {background}
        <div className="proposal-container co-public-message" style={{ textAlign: 'center', paddingTop: 80 }}>
          <h2>Change order not found</h2>
          <p>This link may be invalid or the change order is no longer available.</p>
        </div>
      </div>
    );
  }

  if (co.status === 'draft' && !previewMode) {
    return (
      <div className="proposal-public-page">
        {background}
        <div className="proposal-container co-public-message" style={{ textAlign: 'center', paddingTop: 80 }}>
          <h2>Change order not yet available</h2>
          <p>This change order has not been released for signature yet.</p>
        </div>
      </div>
    );
  }

  if ((co.status === 'void' || co.status === 'rejected') && !previewMode) {
    return (
      <div className="proposal-public-page">
        {background}
        <div className="proposal-container co-public-message" style={{ textAlign: 'center', paddingTop: 80 }}>
          <h2>This change order is no longer active</h2>
          <p>Please contact us if you believe this is a mistake.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="proposal-public-page">
      {background}
      <div className="proposal-container">
        <div className="proposal-header-module">
          <h1 className="proposal-company-name">{co.projectName || co.clientName || 'Change Order'}</h1>
          <p className="header-message co-public-eyebrow">
            {getChangeOrderReference(co)} · Change Order
          </p>
        </div>

        <div className="client-info-section">
          <div className="client-info-card">
            <span className="prepared-for-label">Prepared for</span>
            <div className="client-name">{co.clientName || '—'}</div>
            {co.projectNumber && <div className="project-number">Project #{co.projectNumber}</div>}
            <div className="project-name">{co.title}</div>
          </div>
        </div>

        {co.scopeDescription && (
          <div className="investment-section">
            <h2 className="section-heading">Scope of Work</h2>
            <p style={{ lineHeight: 1.6, color: 'rgba(255,255,255,0.82)', whiteSpace: 'pre-wrap' }}>{co.scopeDescription}</p>
          </div>
        )}

        <div className="investment-section">
          <h2 className="section-heading">Change Order Details</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {rows.map((r, i) => (
              <div className="breakdown-item" key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span>
                  {r.desc}
                  <span className="qty-badge" style={{ marginLeft: 8, opacity: 0.7 }}>{r.cat}</span>
                </span>
                <span style={{ fontWeight: 600 }}>{fmt(r.amount)}</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid rgba(201,162,39,0.35)', paddingTop: 14, color: 'rgba(255,255,255,0.82)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Current contract value</span>
              <span>{fmt(priorContractValue)}</span>
            </div>
            {bondingAdjustment !== 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Scope change</span>
                  <span>{scopeDelta >= 0 ? '+' : ''}{fmt(scopeDelta)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Bonding adjustment ({formatBondingPercent(Number(co.bondingPercent) || 0)})</span>
                  <span>{bondingAdjustment >= 0 ? '+' : ''}{fmt(bondingAdjustment)}</span>
                </div>
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#f0f6fc' }}>
              <span>This change order</span>
              <span style={{ color: co.deltaAmount < 0 ? '#e7b6ae' : '#d4b94a' }}>{co.deltaAmount >= 0 ? '+' : ''}{fmt(co.deltaAmount)}</span>
            </div>
          </div>

          <div className="investment-total" style={{ marginTop: 14 }}>
            <span>New Contract Value</span>
            <span className="total-amount">{fmt(newContractValue)}</span>
          </div>

          <p style={{ marginTop: 12, fontSize: '0.85rem', color: 'rgba(255,255,255,0.65)' }}>
            {changeOrderBillsImmediately(co)
              ? 'Billing: this change order is invoiced separately, due upon approval.'
              : co.deltaAmount < 0
                ? 'Billing: this credit is applied against your remaining contract payment installments — no separate invoice.'
                : 'Billing: this amount folds into your remaining contract payment installments — no separate invoice.'}
          </p>
        </div>

        {/* Signature */}
        <div className="signature-section">
          <h2 className="section-heading">Approval</h2>
          {isSigned ? (
            <div className={`already-signed${co.internalApproval && !co.signature ? ' internal-approval' : ''}`}>
              <div className="signed-badge">
                <CheckCircle2 size={20} />
                {co.internalApproval && !co.signature ? 'Approved' : 'Signed & Approved'}
              </div>
              <div className="signature-details">
                {co.signature?.dataUrl && <img className="signature-image" src={co.signature.dataUrl} alt="Signature" />}
                {co.signature ? (
                  <p>Signed by {co.signature.signedBy}</p>
                ) : isInternallyApprovedChangeOrder(co) ? (
                  <div className="co-public-internal-approval">
                    <ChangeOrderInternalBadge size="md" recordedBy={co.internalApprovedBy} />
                    <p>
                      This change order was approved based on your offline agreement.
                      {co.internalApprovedBy ? ` Recorded by ${co.internalApprovedBy}.` : ''}
                    </p>
                  </div>
                ) : null}
              </div>
              <button className="download-signed-btn" onClick={handleDownload}>
                <Download size={16} /> Download {co.signature ? 'Signed' : 'Approved'} Change Order
              </button>
            </div>
          ) : previewMode ? (
            <div className="signature-pad-wrapper">
              <p style={{ marginBottom: 12, color: 'rgba(255,255,255,0.82)' }}>
                By signing here, the client authorizes this change in scope and the corresponding{' '}
                {co.deltaAmount >= 0 ? 'increase' : 'decrease'} to the contract value of{' '}
                <strong style={{ color: '#fff' }}>{fmt(Math.abs(co.deltaAmount))}</strong>.
              </p>
              <div
                style={{
                  border: '1px dashed rgba(44,62,47,0.35)',
                  borderRadius: 8,
                  padding: '28px 16px',
                  textAlign: 'center',
                  color: '#6B6960',
                  background: '#F7F5F0',
                }}
              >
                Signature pad — shown to the client on the live signing page (preview).
              </div>
            </div>
          ) : (
            <div className="signature-pad-wrapper">
              <p style={{ marginBottom: 12, color: 'rgba(255,255,255,0.82)' }}>
                By signing below, you authorize this change in scope and the corresponding{' '}
                {co.deltaAmount >= 0 ? 'increase' : 'decrease'} to your contract value of{' '}
                <strong style={{ color: '#fff' }}>{fmt(Math.abs(co.deltaAmount))}</strong>.
              </p>
              {signError && <p style={{ color: '#ffb4ab', marginBottom: 10 }}>{signError}</p>}
              <SignaturePad onSign={handleSign} disabled={signing} />
            </div>
          )}
        </div>

        <div className="proposal-footer">
          <button className="download-proposal-btn" onClick={handleDownload}>
            <FileText size={16} /> View change order PDF
          </button>
        </div>
      </div>
    </div>
  );
}
